import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';

import PlanSelection from './sections/PlanSelection/PlanSelection';
import SelectLoadMethod from './sections/SelectLoadMethod/SelectLoadMethod';
import SubscriptionByEmails from './sections/SubscriptionByEmails/SubscriptionByEmails';
import SubscriptionByFile from './sections/SubscriptionByFile/SubscriptionByFile';
import SubscriptionCheckout from './sections/SubscriptionCheckout/SubscriptionCheckout';
import BdsLinearProgressIndicator from '../../../shared/components/v3/BdsLinearProgressIndicator/BdsLinearProgressIndicator';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import { MANAGE_COLLABORATORS_ROUTE } from '../../../shared/constants/auth';
import {
  MANAGE_COLLABORATORS_UPLOAD_COLLABORATORS_TITLE,
  MANAGE_COLLABORATORS_UPLOAD_TOTAL_STEPS,
  DEFAULT_COMPANY_PLAN_VALUE,
  SKIP_LOAD_METHOD
} from '../../../shared/constants/manageCollaborators';
import { PlansContext } from '../../../shared/context/plans/plansContext';
import { EmailsData } from '../../../shared/interfaces/manageCollaborators';
import { ICompanyPlan } from '../../../shared/interfaces/plans';
import { ManageCollaboratorsUploadMethods, ManageCollaboratorsUploadSteps } from '../../../shared/types/types';
import '../ManageCollaborators.scss';

const SubscribeCollaborators = () => {
  const [currentUploadStep, setCurrentUploadStep] = useState<ManageCollaboratorsUploadSteps>(0);
  const [uploadMethod, setUploadMethod] = useState<ManageCollaboratorsUploadMethods>();
  const [selectedPlan, setSelectedPlan] = useState<ICompanyPlan>(DEFAULT_COMPANY_PLAN_VALUE);
  const [emailsData, setEmailsData] = useState<EmailsData>({ emailsToUpload: [], emailsOmmited: [], requestId: null });

  const { plans } = useContext(PlansContext);

  const navigate = useNavigate();

  const hasSinglePlan = plans?.length === 1;
  const skipToFile = selectedPlan && SKIP_LOAD_METHOD.includes(selectedPlan.type);
  const skipToEmail = selectedPlan && !selectedPlan.countryCode;

  const getHeroData = useMemo(() => {
    const isUploadByFileStep = currentUploadStep === 3 && uploadMethod === 'upload-file';

    if (isUploadByFileStep) {
      return {
        title: MANAGE_COLLABORATORS_UPLOAD_COLLABORATORS_TITLE[3]?.title ?? '',
        subtitle: MANAGE_COLLABORATORS_UPLOAD_COLLABORATORS_TITLE[3]?.subtitle2 ?? ''
      };
    }

    return MANAGE_COLLABORATORS_UPLOAD_COLLABORATORS_TITLE[currentUploadStep] ?? { title: '', subtitle: '' };
  }, [currentUploadStep, uploadMethod]);

  const progress = useMemo(() => {
    return Math.min(((currentUploadStep + 2) / MANAGE_COLLABORATORS_UPLOAD_TOTAL_STEPS) * 100, 100);
  }, [currentUploadStep]);

  const handleNextStep = useCallback(() => {
    if (currentUploadStep === 0 && skipToFile) {
      setUploadMethod('upload-file');
      setCurrentUploadStep(2);
    } else if (currentUploadStep === 0 && skipToEmail) {
      setUploadMethod('upload-email');
      setCurrentUploadStep(2);
    } else {
      const step = (currentUploadStep + 1) as ManageCollaboratorsUploadSteps;
      setCurrentUploadStep(step);
    }
  }, [currentUploadStep, skipToFile, skipToEmail]);

  const handleActionBack = useCallback(() => {
    if (
      currentUploadStep === 0 ||
      (currentUploadStep === 1 && hasSinglePlan) ||
      (currentUploadStep === 2 && hasSinglePlan && (skipToEmail || skipToFile))
    ) {
      navigate(MANAGE_COLLABORATORS_ROUTE, { state: { method: 'addCollaborators' } });
      return;
    }

    if (currentUploadStep === 2 && (uploadMethod === 'upload-email' || uploadMethod === 'upload-file')) {
      setEmailsData({ emailsToUpload: [], emailsOmmited: [] });
    }

    if (currentUploadStep === 2 && (skipToFile || skipToEmail)) {
      setUploadMethod(undefined);
      setCurrentUploadStep(0);
    } else {
      const step = (currentUploadStep - 1) as ManageCollaboratorsUploadSteps;
      setCurrentUploadStep(step);
    }
  }, [currentUploadStep, skipToFile, skipToEmail, uploadMethod, hasSinglePlan, navigate]);

  const handleUploadMethodSelected = (method: ManageCollaboratorsUploadMethods) => {
    setUploadMethod(method);
  };

  const handleSelectedPlan = (plan: ICompanyPlan) => {
    setSelectedPlan(plan);
  };

  useEffect(() => {
    const skipEmail = hasSinglePlan && !plans[0].countryCode;
    const skipFile = hasSinglePlan && SKIP_LOAD_METHOD.includes(plans[0].type);

    if (hasSinglePlan && (skipEmail || skipFile)) {
      setUploadMethod(skipFile ? 'upload-file' : 'upload-email');
      setCurrentUploadStep(2);
      setSelectedPlan(plans[0]);
    } else if (hasSinglePlan) {
      setSelectedPlan(plans[0]);
      setCurrentUploadStep(1);
    } else {
      setCurrentUploadStep(0);
    }
  }, [hasSinglePlan, plans]);

  const renderTypeUploadStep = useCallback(() => {
    if (uploadMethod === 'upload-email') {
      return (
        <SubscriptionByEmails
          nextStep={handleNextStep}
          emailsData={emailsData}
          setEmailsToUpload={setEmailsData}
          planSelected={selectedPlan}
        />
      );
    } else {
      return (
        <SubscriptionByFile
          nextStep={handleNextStep}
          emailsData={emailsData}
          setEmailsToUpload={setEmailsData}
          planSelected={selectedPlan}
        />
      );
    }
  }, [uploadMethod, emailsData, handleNextStep, selectedPlan]);

  return (
    <div className="manage-collaborators-container">
      <SecondaryHero
        title={t(getHeroData.title, { count: 2 })}
        description={t(getHeroData.subtitle || '', { count: 2 })}
        color="surface-container-quaternary-light"
        bottomContent={<BdsLinearProgressIndicator variant="subtle" size="s" progress={progress} />}
        backAction={handleActionBack}
      />
      <div className="manage-collaborators-content">
        {currentUploadStep === 0 && (
          <PlanSelection
            onChangeSelectedPlan={handleSelectedPlan}
            selectedPlan={selectedPlan}
            nextStep={handleNextStep}
          />
        )}
        {currentUploadStep === 1 && (
          <SelectLoadMethod
            uploadMethod={uploadMethod}
            onUploadMethodSelected={handleUploadMethodSelected}
            nextStep={handleNextStep}
          />
        )}
        {currentUploadStep === 2 && renderTypeUploadStep()}
        {currentUploadStep === 3 && (
          <SubscriptionCheckout
            emailsData={emailsData}
            selectedPlan={selectedPlan}
            previousStep={handleActionBack}
            uploadMethod={uploadMethod}
          />
        )}
      </div>
    </div>
  );
};

export default SubscribeCollaborators;
